import { DataWithPagination } from "services/api/types";
import { Pagination } from "services/search";
import { PermissionCategory, PermissionRow, User, UserEditFormValues, UserEmailExists, UserPermission, UserPermissionEditFormValues, UserPermissionRow, UserRole, UserRow } from "./types";
import {
  transformPermissionRow,
  transformUser,
  transformUserCreate,
  transformUserEmailExists,
  transformUserPermission,
  transformUserPermissionCreate,
  transformUserPermissionUpdate,
  transformUserRoles,
  transformUserRow,
  transformUserUpdate,
} from "./transformations";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "../transformation";
import { MfaChallenge } from "services/auth/types";
import { transformMfaChallenge } from "services/auth/transformations";

/*---------------------------------------------------------------------------------------
  USER SEARCH
-----------------------------------------------------------------------------------------*/
export const getUsersAPI = async (
  pagination: Pagination,
  name?: string | null,
  email?: string | null,
  phone?: string | null,
  address?: string | null,
  roleId?: string | null
): Promise<DataWithPagination<UserRow>> => {
  let path = `/v1/users?query=`;
  if (name) path += `${name}`;
  if (email) path += `&email=${email}`;
  if (phone) path += `&phone=${phone}`;
  if (address) path += `&address=${address}`;
  if (roleId) path += `&roleId=${roleId}`;

  return await paginatedApiCall(path, pagination, transformUserRow);
};

/*---------------------------------------------------------------------------------------
    USER - BY ID
-----------------------------------------------------------------------------------------*/
export const getUserAPI = async (userId: string): Promise<User> => {
  const response = await createApiCall(
    {
      path: `/v1/users/${userId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();


  return transformUser(response.data);
};

/*---------------------------------------------------------------------------------------
    USER - PROVJERA POSTOJANJA EMAIL ADRESE
-----------------------------------------------------------------------------------------*/
export const checkUserEmailExistsAPI = async (email: string): Promise<UserEmailExists> => {
  try {
    // Bez snackbar konfiguracije — provjera je tiha, greška se ne prikazuje korisniku.
    const response = await createApiCall({
      path: `/v1/users/email-exists?email=${encodeURIComponent(email)}`,
      method: "GET",
    })();

    return transformUserEmailExists(response.data);
  } catch {
    // Provjera ne smije blokirati wizard — serverska provjera pri spremanju ostaje.
    return transformUserEmailExists(null);
  }
};

/*---------------------------------------------------------------------------------------
    USER - PROVJERA POSTOJANJA KORISNIČKOG IMENA
    Kod uređivanja se prosljeđuje ID korisnika, da se njegovo vlastito ime ne računa kao zauzeto.
-----------------------------------------------------------------------------------------*/
export const checkUserNameExistsAPI = async (
  username: string,
  exceptUserId?: string | null
): Promise<UserEmailExists> => {
  try {
    let path = `/v1/users/username-exists?username=${encodeURIComponent(username)}`;
    if (exceptUserId) path += `&exceptUserId=${encodeURIComponent(exceptUserId)}`;

    // Bez snackbar konfiguracije — provjera je tiha, greška se ne prikazuje korisniku.
    const response = await createApiCall({ path, method: "GET" })();

    return transformUserEmailExists(response.data);
  } catch {
    // Provjera ne smije blokirati wizard — serverska provjera pri spremanju ostaje.
    return transformUserEmailExists(null);
  }
};

/*---------------------------------------------------------------------------------------
    USER - CREATE
    Ako je zadan tenantId, korisnik se kreira u kontekstu tog tenanta.
-----------------------------------------------------------------------------------------*/
export const createUserAPI = async (
  userEditFormValues: UserEditFormValues,
  tenantId?: string | null
): Promise<User> => {
  const path = tenantId
    ? `/v1/users/tenants/${tenantId}/users`
    : `/v1/users`;

  const response = await createApiCall(
    {
      path,
      method: "POST",
      body: transformUserCreate(userEditFormValues),
    },
    {
      getMessage: {
        success: () => "Bravo! Uspješno ste kreirali korisnika",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformUser(response.data);
};

/*---------------------------------------------------------------------------------------
    USER - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateUserAPI = async (
  userId: string,
  userEditFormValues: UserEditFormValues
): Promise<User> => {
  const response = await createApiCall(
    {
      path: `/v1/users/${userId}`,
      method: "PATCH",
      body: transformUserUpdate(userEditFormValues),
    },
    {
      getMessage: {
        success: () => "Bravo! Uspješno ste ažurirali korisnika",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformUser(response.data);
};


/*---------------------------------------------------------------------------------------
    CURRENT USER (Moj račun) - UPDATE PROFILE
-----------------------------------------------------------------------------------------*/
export const updateCurrentUserAPI = async (values: {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}): Promise<User> => {
  const response = await createApiCall(
    {
      path: `/v1/users/current`,
      method: "PATCH",
      body: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      },
    },
    {
      getMessage: {
        success: () => "Podaci računa su uspješno spremljeni",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformUser(response.data);
};

/*---------------------------------------------------------------------------------------
    CURRENT USER (Moj račun) - CHANGE PASSWORD
-----------------------------------------------------------------------------------------*/
export const changeCurrentPasswordAPI = async (
  password: string
): Promise<boolean> => {
  await createApiCall(
    {
      path: `/v1/users/current/password`,
      method: "PATCH",
      body: { password },
    },
    {
      getMessage: {
        success: () => "Lozinka je uspješno promijenjena",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return true;
};

/*---------------------------------------------------------------------------------------
    CURRENT USER (Moj račun) - DVOSTRUKA PROVJERA PRIJAVE: SLANJE KODA
    Prvi korak uključivanja. Zaštita se pali tek kad se kod vrati kroz updateCurrentMfaAPI,
    da se nitko ne zaključa s adresom na koju pošta ne stiže.
-----------------------------------------------------------------------------------------*/
export const startCurrentMfaChallengeAPI = async (): Promise<MfaChallenge> => {
  const response = await createApiCall(
    {
      path: `/v1/users/current/mfa/challenge`,
      method: "POST",
    },
    {
      getMessage: {
        success: () => "Kod je poslan na vašu e-mail adresu",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformMfaChallenge(response.data);
};

/*---------------------------------------------------------------------------------------
    CURRENT USER (Moj račun) - DVOSTRUKA PROVJERA PRIJAVE: UKLJUČI / ISKLJUČI
    Uključivanje traži kod iz startCurrentMfaChallengeAPI; isključivanje ne.
-----------------------------------------------------------------------------------------*/
export const updateCurrentMfaAPI = async (values: {
  enabled: boolean;
  challengeId?: string | null;
  code?: string | null;
}): Promise<User> => {
  const response = await createApiCall(
    {
      path: `/v1/users/current/mfa`,
      method: "PATCH",
      body: {
        enabled: values.enabled,
        challengeId: values.challengeId ?? null,
        code: values.code ?? null,
      },
    },
    {
      getMessage: {
        success: () =>
          values.enabled
            ? "Dvostruka provjera prijave je uključena"
            : "Dvostruka provjera prijave je isključena",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformUser(response.data);
};

/*---------------------------------------------------------------------------------------
    USER - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteUserAPI = async (
  id: string,
  t: (key: string) => string
): Promise<User> => {
  const response = await createApiCall(
    {
      path: `/v1/users/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.user.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformUser(response.data);
};

/*---------------------------------------------------------------------------------------
  PERMISSION SEARCH
-----------------------------------------------------------------------------------------*/

export const getPermissionsAPI = async (
  pagination: Pagination,
  name?: string | null,
  categoryIds?: string[],
  includeSubcategories?: boolean
): Promise<DataWithPagination<PermissionRow>> => {
  let path = `/v1/permissions?name=`;

  if (name) path += `${name}`;
  if (categoryIds && categoryIds.length > 0) {
    categoryIds.forEach(catId => {
      path += `&categoryIds=${catId}`;
    });
  }
  if (includeSubcategories) path += `&includeSubcategories=true`;

  return await paginatedApiCall(path, pagination, transformPermissionRow);
};

/*---------------------------------------------------------------------------------------
  PERMISSION CATEGORIES
-----------------------------------------------------------------------------------------*/

export const getPermissionCategoriesAPI = async (): Promise<PermissionCategory[]> => {
  const response = await createApiCall(
    {
      path: `/v1/permissions/categories/all`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  // Handle both PascalCase and camelCase response
  return response.data?.categories || response.data?.Categories || [];
};

/*---------------------------------------------------------------------------------------
  USER PERMISSION SEARCH
-----------------------------------------------------------------------------------------*/

export const getUserPermissionsAPI = async (
  userId: string,
): Promise<UserPermissionRow[]> => {

  const response = await createApiCall(
    {
      path:`/v1/users/${userId}/permissions`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.permissions?.map(transformUserPermission);
};

/*---------------------------------------------------------------------------------------
    USER PERMISSION - BY ID
-----------------------------------------------------------------------------------------*/

export const getUserPermissionAPI = async (userId: string, permissionId: string): Promise<UserPermission> => {
  const response = await createApiCall(
    {
      path: `/v1/users/${userId}/permission/${permissionId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();


  return transformUserPermission(response.data);
};

/*---------------------------------------------------------------------------------------
    USER PERMISSION - CREATE
-----------------------------------------------------------------------------------------*/
export const createUserPermissionAPI = async (
  userId: string,
  userEditFormValues: UserPermissionEditFormValues
): Promise<UserPermission> => {
  const response = await createApiCall(
    {
      path: `/v1/users/${userId}/permission`,
      method: "POST",
      body: transformUserPermissionCreate(userEditFormValues),
    },
    {
      getMessage: {
        success: () => "Bravo! Uspješno ste kreirali korisničko pravo",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformUserPermission(response.data);
};

/*---------------------------------------------------------------------------------------
    USER PERMISSION - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateUserPermissionAPI = async (
  userId: string,
  permissionId: string,
  userPermissionEditFormValues: UserPermissionEditFormValues
): Promise<UserPermission> => {
  const response = await createApiCall(
    {
      path: `/v1/users/${userId}/permission/${permissionId}`,
      method: "PUT",
      body: transformUserPermissionUpdate(userPermissionEditFormValues),
    },
    {
      getMessage: {
        success: () => "Bravo! Uspješno ste ažurirali korisničko pravo",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformUserPermission(response.data);
};


/*---------------------------------------------------------------------------------------
    USER PERMISSION - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteUserPermissionAPI = async (userId: string, permissionId: string): Promise<UserPermission> => {
  const response = await createApiCall(
    {
      path: `/v1/users/${userId}/permission/${permissionId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Korisničko pravo je uspješno obrisano",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformUserPermission(response.data);
};

/*---------------------------------------------------------------------------------------
    USER PERMISSION - BULK UPDATE (ADD/REMOVE)
-----------------------------------------------------------------------------------------*/
export interface BulkPermissionUpdateRequest {
  grantedPermissionIds: string[];
  deniedPermissionIds: string[];
}

export const bulkUpdateUserPermissionsAPI = async (
  userId: string,
  request: BulkPermissionUpdateRequest
): Promise<void> => {
  await createApiCall(
    {
      path: `/v1/users/${userId}/permissions/bulk`,
      method: "POST",
      body: request,
    },
    {
      getMessage: {
        success: () => "Korisnička prava su uspješno ažurirana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
};


/*---------------------------------------------------------------------------------------
    USER - ROLSE
-----------------------------------------------------------------------------------------*/
export const getUserRolesAPI = async (): Promise<UserRole[]> => {
  const response = await createApiCall(
    {
      path: `/v1/users/roles`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();


  return response.data.map(transformUserRoles);
};